/**
 * Impuestos en el panel inicial: lo generado en el periodo y lo pendiente de pagar a Hacienda
 * (IVA, IRPF e Impuesto de Sociedades), LEÍDO DEL LIBRO y rotulado. Solo ADMIN (la API lo exige y
 * aquí además no se pide a los demás roles).
 *
 * ☠️ Cada cifra dice de dónde sale: lo que es del libro va tal cual y la ESTIMACIÓN del Impuesto de
 * Sociedades lleva su etiqueta «ESTIMADO» y se enseña como un RANGO (15 % de sociedad nueva — 25 %
 * general): sin ajustes fiscales y sin tipo confirmado por la gestoría, una cifra única sería una
 * invención en un panel de dirección. El asiento real (63000 / 47502 / 47300) lo hace la gestoría al
 * cierre. Si un pago a Hacienda no está asentado, el «pendiente» sale inflado: el pie lo dice.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, Landmark, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TaxPosition } from '@jr2os/shared'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'

export default function TaxesCard({
  year,
  quarter,
  month,
}: {
  year: number
  quarter: number | ''
  month: number | ''
}) {
  const { t } = useTranslation('dashboard')
  const [data, setData] = useState<TaxPosition | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    const qs = new URLSearchParams({ year: String(year) })
    if (month) qs.set('month', String(month))
    else if (quarter) qs.set('quarter', String(quarter))
    setError(null)
    api
      .get<TaxPosition>(`/stats/dashboard-taxes?${qs}`)
      .then((r) => {
        if (!ctrl.signal.aborted) setData(r)
      })
      .catch((err: unknown) => {
        if (!ctrl.signal.aborted)
          setError(err instanceof Error ? err.message : t('overview.errorLoading'))
      })
    return () => ctrl.abort()
  }, [year, quarter, month, t])

  const m = (n: number) => formatCents(n)

  if (error) {
    return (
      <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
        <AlertTriangle size={14} /> {error}
      </div>
    )
  }
  if (!data) {
    return (
      <div className="surface p-4 t-muted text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> {t('loading')}
      </div>
    )
  }

  const { vat, irpf, corporateTax: cit } = data
  const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
    <div className="flex items-baseline justify-between gap-3 text-xs py-0.5">
      <span className="t-muted">{label}</span>
      <span className={`font-mono ${strong ? 't-primary font-semibold' : 't-secondary'}`}>
        {value}
      </span>
    </div>
  )
  const Block = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="surface p-4">
      <h4 className="text-[11px] uppercase tracking-wider t-muted mb-2">{title}</h4>
      {children}
    </div>
  )
  const Sub = ({ children }: { children: React.ReactNode }) => (
    <div className="text-[10px] uppercase tracking-wider t-faint mt-2 mb-0.5">{children}</div>
  )

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
          <Landmark size={14} className="text-jr2-700 dark:text-jr2-300" />
          {t('overview.taxes.title')}
        </h3>
        <div className="text-xs t-muted">
          {t('overview.taxes.total')}:{' '}
          <span className="font-mono t-primary font-semibold">{m(data.totalPendingCents)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Block title={t('overview.taxes.vat')}>
          <Sub>{t('overview.taxes.generated')}</Sub>
          <Row
            label={t('overview.taxes.vatRepercutido')}
            value={m(vat.generated.repercutidoCents)}
          />
          <Row label={t('overview.taxes.vatSoportado')} value={m(vat.generated.soportadoCents)} />
          <Row label={t('overview.taxes.vatNet')} value={m(vat.generated.netCents)} strong />
          <Sub>{t('overview.taxes.pending')}</Sub>
          <Row label={t('overview.taxes.vatPayable')} value={m(vat.payableCents)} strong />
          <Row label={t('overview.taxes.vatOpen')} value={m(vat.openQuarterCents)} />
          {vat.openQuarterCents < 0 && (
            <div className="text-[10px] t-faint">{t('overview.taxes.vatOpenCompensate')}</div>
          )}
          {vat.receivableCents !== 0 && (
            <Row label={t('overview.taxes.vatReceivable')} value={m(vat.receivableCents)} />
          )}
          <div className="text-[10px] t-faint mt-2">
            {t('overview.taxes.nextDeadline', {
              date: vat.nextDeadline.date,
              label: vat.nextDeadline.label,
            })}
          </div>
        </Block>

        <Block title={t('overview.taxes.irpf')}>
          <Sub>{t('overview.taxes.generated')}</Sub>
          <Row label={t('overview.taxes.irpfGenerated')} value={m(irpf.generatedCents)} strong />
          <Sub>{t('overview.taxes.pending')}</Sub>
          <Row label={t('overview.taxes.irpfPayable')} value={m(irpf.payableCents)} strong />
          {irpf.receivableCents > 0 && (
            <Row label={t('overview.taxes.irpfReceivable')} value={m(irpf.receivableCents)} />
          )}
        </Block>

        <Block title={t('overview.taxes.corporate')}>
          <Sub>{t('overview.taxes.booked')}</Sub>
          <Row label={t('overview.taxes.isExpense')} value={m(cit.booked.expenseCents)} />
          <Row label={t('overview.taxes.isPayable')} value={m(cit.booked.payableCents)} strong />
          <Row label={t('overview.taxes.isOnAccount')} value={m(cit.booked.paidOnAccountCents)} />
          <Sub>
            {t('overview.taxes.isEstimate')}{' '}
            <span className="ml-1 rounded px-1 py-0.5 bg-amber-500/15 text-amber-700 dark:text-amber-300 normal-case tracking-normal">
              {t('overview.taxes.estimated')}
            </span>
          </Sub>
          <Row
            label={t('overview.taxes.isRange', {
              low: cit.estimate.lowRatePct,
              high: cit.estimate.highRatePct,
            })}
            value={`${m(cit.estimate.lowCents)} – ${m(cit.estimate.highCents)}`}
            strong
          />
          <div className="text-[10px] t-faint mt-1">
            {t('overview.taxes.isEstimateNote', { base: m(cit.estimate.baseCents) })}
          </div>
        </Block>
      </div>
      <p className="text-[10px] t-faint">{t('overview.taxes.disclaimer')}</p>
    </section>
  )
}
